import React, { useMemo } from 'react';
import { IMaskInput } from 'react-imask';
import { CreditCard } from 'lucide-react';
import { TextInput } from './Inputs/TextInput';
import { CreditCardFieldsProps } from '../Types';
import { formatCurrency } from '../Utils/format';
import { CHECKOUT_FIELD_LIMITS } from '../Constants/checkoutFieldLimits';
import { CardBrandBadge, detectCardBrand, getMaxParcelas } from '../Utils/cardBrand';

export const CreditCardFields: React.FC<CreditCardFieldsProps> = ({
  method,
  finalValue,
  values,
  errors,
  onChange,
  hostedMode = false,
}) => {
  const maxParcelas = useMemo(() => getMaxParcelas(method), [method]);
  const cardBrand = useMemo(() => detectCardBrand(values.cardNumber), [values.cardNumber]);
  const cvvMask = cardBrand?.code?.size === 4 ? '0000' : '000';
  const installmentOptions = useMemo(
    () => Array.from({ length: maxParcelas }, (_, i) => i + 1),
    [maxParcelas]
  );

  return (
    <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 backdrop-blur-sm">
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
        <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-lg flex items-center justify-center">
          <CreditCard className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
          Dados do Cartão
        </h3>
      </div>

      <div className="space-y-4">
        {hostedMode && (
          <div className="bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-xl p-4">
            <p className="text-sm text-sky-800 leading-relaxed">
              Você será redirecionado ao <span className="font-bold">ambiente seguro do banco</span> para
              informar os dados do cartão e concluir o pagamento. Depois, retornará automaticamente para esta página.
            </p>
          </div>
        )}

        {!hostedMode && (
          <>
        <div>
          <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
            Número do Cartão <span className="text-red-500">*</span>
          </label>
          <div className="relative mt-1">
            <IMaskInput
              mask="0000 0000 0000 0000 000"
              value={values.cardNumber}
              onAccept={(val: string) => onChange('cardNumber', val)}
              placeholder="0000 0000 0000 0000"
              className={`w-full px-4 pr-12 py-2 h-[42px] rounded-lg border bg-white ${
                errors.cardNumber ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-1 focus:ring-blue-300`}
            />
            <CardBrandBadge brandType={cardBrand?.type ?? null} insideInput />
          </div>
          {errors.cardNumber && <p className="text-red-500 text-sm mt-1">{errors.cardNumber}</p>}
        </div>

        <TextInput
          name="cardHolderName"
          label="Nome Impresso no Cartão"
          value={values.cardHolderName}
          onChange={(e) => onChange('cardHolderName', e.target.value)}
          errorMessage={errors.cardHolderName}
          placeholder="Como está no cartão"
          requiredMark
          maxLength={CHECKOUT_FIELD_LIMITS.cardHolderName}
          labelSize="sm"
          labelColor="gray-600"
          labelWeight="medium"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
              Validade <span className="text-red-500">*</span>
            </label>
            <IMaskInput
              mask="00/00"
              value={values.cardExpiry}
              onAccept={(val: string) => onChange('cardExpiry', val)}
              placeholder="MM/AA"
              className={`w-full px-4 mt-1 py-2 h-[42px] rounded-lg border bg-white ${
                errors.cardExpiry ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-1 focus:ring-blue-300`}
            />
            {errors.cardExpiry && <p className="text-red-500 text-sm mt-1">{errors.cardExpiry}</p>}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
              Código de Segurança <span className="text-red-500">*</span>
            </label>
            <IMaskInput
              mask={cvvMask}
              value={values.cardCvv}
              onAccept={(val: string) => onChange('cardCvv', val)}
              placeholder={cvvMask === '0000' ? '0000' : '000'}
              className={`w-full px-4 mt-1 py-2 h-[42px] rounded-lg border bg-white ${
                errors.cardCvv ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-1 focus:ring-blue-300`}
            />
            {errors.cardCvv && <p className="text-red-500 text-sm mt-1">{errors.cardCvv}</p>}
          </div>
        </div>
          </>
        )}

        <div>
          <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
            Número de Parcelas <span className="text-red-500">*</span>
          </label>
          <select
            value={values.installments}
            onChange={(e) => onChange('installments', parseInt(e.target.value, 10))}
            className={`w-full px-4 mt-1 py-2 h-[42px] rounded-lg border bg-white ${
              errors.installments ? 'border-red-500' : 'border-gray-300'
            } focus:outline-none focus:ring-1 focus:ring-blue-300`}
          >
            {installmentOptions.map((n) => (
              <option key={n} value={n}>
                {n}x de {formatCurrency(finalValue / n)}
                {n === 1 ? ' (à vista)' : ''}
              </option>
            ))}
          </select>
          {errors.installments && <p className="text-red-500 text-sm mt-1">{errors.installments}</p>}
        </div>
      </div>
    </div>
  );
};
